import { useState, useEffect } from 'react';

type InstallState = 'hidden' | 'installable' | 'installed';

export function usePWAInstall() {
    const [installState, setInstallState] = useState<InstallState>('hidden');
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Detect Standalone/Installed
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || (navigator as any).standalone === true;

        if (isStandalone) {
            setInstallState('installed');
            return;
        }

        // Detect iOS
        const ua = window.navigator.userAgent;
        const _isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
        setIsIOS(_isIOS);
        if (_isIOS) {
            // iOS doesn't support beforeinstallprompt, but we can show instructions anytime
            setInstallState('installable');
            return;
        }

        // Detect Desktop/Android PWA readiness
        // Note: 'beforeinstallprompt' fires if not installed and meets PWA criteria
        const handler = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setInstallState('installable');
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Listen for successful install
        const installHandler = () => {
            setInstallState('installed');
            setDeferredPrompt(null);
        };
        window.addEventListener('appinstalled', installHandler);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', installHandler);
        };
    }, []);

    const promptInstall = async () => {
        if (isIOS) {
            return 'ios';
        }

        if (!deferredPrompt) {
            return 'unavailable';
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setInstallState('installed');
        }
        setDeferredPrompt(null);
        return outcome;
    };

    // Banner Logic
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        if (installState !== 'installable') {
            setShowBanner(false);
            return;
        }

        const dismissedUntil = localStorage.getItem('pwa_install_dismissed_until');
        if (dismissedUntil && Date.now() < Number(dismissedUntil)) {
            return;
        }

        const triggerBanner = () => {
            // 10s after first interaction
            const timer = setTimeout(() => {
                setShowBanner(true);
            }, 10000);
            return () => clearTimeout(timer);
        };

        const onInteract = () => {
            window.removeEventListener('click', onInteract);
            window.removeEventListener('keydown', onInteract);
            window.removeEventListener('touchstart', onInteract);
            triggerBanner();
        };

        window.addEventListener('click', onInteract);
        window.addEventListener('keydown', onInteract);
        window.addEventListener('touchstart', onInteract);

        return () => {
            window.removeEventListener('click', onInteract);
            window.removeEventListener('keydown', onInteract);
            window.removeEventListener('touchstart', onInteract);
        };
    }, [installState]);

    const dismissBanner = () => {
        setShowBanner(false);
        const until = Date.now() + (7 * 24 * 60 * 60 * 1000);
        localStorage.setItem('pwa_install_dismissed_until', String(until));
    };

    return {
        installState,
        promptInstall,
        isIOS,
        showBanner,
        dismissBanner,
        pwaDiagnostics: {
            isInstalled: installState === 'installed',
            swControlled: 'serviceWorker' in navigator && !!navigator.serviceWorker.controller,
            promptReady: !!deferredPrompt,
            isIOS,
            ua: navigator.userAgent
        }
    };
}
